import { useState, useCallback } from 'react';
import { useReceptionStrings } from '../lib/receptionStrings';
import { useBookingClinics } from './useDoctorBooking';
import { LIVE_STATUSES } from '../lib/receptionRowOps';

// A timed نوبت moves while it waits for its patient; a «خارج از نوبت» one may be
// given its hour while waiting or once the patient is here (PM-0558).
const movableStatus = (apt) => (apt?.schedulingMode === 'QUEUE'
  ? apt.status === 'scheduled' || apt.status === 'arrived'
  : apt?.status === 'scheduled');

// ─────────────────────────────────────────────────────────────────────────────
// پذیرش's half of «ثبت نوبت», «ویرایش نوبت» and «لغو درخواست» — ONE hook for both surfaces, so
// the web day sheet and the phone list cannot disagree about when the doctor may
// book or what a booking says afterwards.
//
// Feedback (DESIGN_CONCEPT §2.4): closing the overlay IS the feedback when the
// new row is on screen. A toast is added only when it is not — the نوبت is on
// another day, or it went to the clinic for approval (the row appears greyed,
// and «در انتظار تأیید کلینیک» is news the doctor has to read once).
// ─────────────────────────────────────────────────────────────────────────────

export function useReceptionBooking({ selISO, reload, withdrawRequest, toast }) {
  const s = useReceptionStrings();
  const booking = useBookingClinics();
  const canBook = (booking.clinics || []).length > 0;

  const [bookOpen, setBookOpen] = useState(false);
  // «نوبت بعدی» from a row (PM-0518): the dialog opens on that patient, at that
  // row's clinic. Null for the header's «نوبت جدید».
  const [bookPreset, setBookPreset] = useState(null);
  const [pendingWithdraw, setPendingWithdraw] = useState(null);
  // «ویرایش نوبت» (PM-0102, PM-0531): the row being edited, or null.
  const [editing, setEditing] = useState(null);

  // The same forecast «ثبت نوبت» makes, for a move: a نوبت still waiting for its
  // patient, at a clinic that lets this doctor book directly (a نوبت that names
  // no clinic is the doctor's own diary). Where the clinic withdrew the grant the
  // move is the desk's. Display only — the BFF decides again from the membership
  // row when it writes.
  //
  // A «خارج از نوبت» row (`QUEUE`, no hour yet) is given its hour with the same
  // field — «تعیین ساعت», PM-0558 — waiting or already here.
  const canReschedule = useCallback((apt) => {
    if (!apt?.id || !movableStatus(apt) || !apt.dateTime) return false;
    if (!apt.clinicId) return true;
    return Boolean((booking.clinics || []).find((c) => c.clinicId === apt.clinicId)?.canBookDirectly);
  }, [booking.clinics]);

  // Why the time field of «ویرایش نوبت» is locked, or null when it is open. The
  // dialog SHOWS the reason instead of hiding the field, so a doctor who cannot
  // move a نوبت is told why rather than left to look for a button.
  const timeLock = useCallback((apt) => {
    if (canReschedule(apt)) return null;
    if (!movableStatus(apt)) return 'status';
    return apt?.dateTime ? 'grant' : 'untimed';
  }, [canReschedule]);

  // Who may open «ویرایش نوبت» at all: a live row (waiting, arrived or in the
  // room) at a clinic that is not read-only — the same bar as «یادداشت نوبت».
  const canEdit = useCallback((apt) => {
    if (!apt?.id || !LIVE_STATUSES.has(apt.status)) return false;
    if (!apt.clinicId) return true;
    return !(booking.clinics || []).find((c) => c.clinicId === apt.clinicId)?.readOnly;
  }, [booking.clinics]);

  // The row is on screen when it stayed on this day: its new hour IS the
  // feedback. Moved to another day it leaves the list, so that is said in words.
  // `time` is present only when this save moved the نوبت.
  const onEdited = useCallback(({ time } = {}) => {
    reload?.();
    if (time?.dayISO && time.dayISO !== selISO) toast?.success?.(s('dashboard.reschedule.moved', { when: time.when || '' }));
  }, [reload, toast, s, selISO]);

  const onBooked = useCallback((result, { dayISO, walkIn } = {}) => {
    reload?.();
    // A walk-in has no hour to find on the list, so it is always confirmed in words.
    if (walkIn) toast?.success?.(s('dashboard.book.walkInBooked'));
    else if (result?.outcome === 'requested') toast?.success?.(s('dashboard.book.requested'));
    else if (dayISO && dayISO !== selISO) toast?.success?.(s('dashboard.book.booked'));
  }, [reload, toast, s, selISO]);

  // Returned to the ConfirmDialog so it stays open until the write settles.
  const confirmWithdraw = useCallback(async () => {
    if (!pendingWithdraw) return;
    try {
      await withdrawRequest(pendingWithdraw.id);
      setPendingWithdraw(null);
    } catch {
      setPendingWithdraw(null);
      toast?.error?.(s('dashboard.withdraw.failed'));
    }
  }, [pendingWithdraw, withdrawRequest, toast, s]);

  return {
    s,
    canBook,
    bookingClinics: booking,
    bookOpen,
    bookPreset,
    // Also bound straight to onClick, so anything that is not a preset (an
    // event) opens an empty dialog.
    openBook: useCallback((preset) => {
      setBookPreset(preset?.patient?.id ? preset : null);
      setBookOpen(true);
    }, []),
    closeBook: useCallback(() => { setBookOpen(false); setBookPreset(null); }, []),
    onBooked,
    pendingWithdraw,
    askWithdraw: setPendingWithdraw,
    cancelWithdraw: useCallback(() => setPendingWithdraw(null), []),
    confirmWithdraw,
    canReschedule,
    canEdit,
    timeLock,
    editing,
    askEdit: setEditing,
    closeEdit: useCallback(() => setEditing(null), []),
    onEdited,
  };
}

export default useReceptionBooking;
